import type { FlatItem } from './types';
import type { LineItemData } from './LineItemList';
import type { SubItemForm } from './subItemTypes';
import { calculateTotalCost, type Rates, type SubItemTypeMetaMap } from '@jobos/shared';
import type { SubItemCategory } from '@jobos/shared';

export interface BreakdownClipboard {
  type: 'jobos-breakdown';
  version: 1;
  items: FlatItem[];
}

export function serializeForClipboard(items: FlatItem[]): string {
  const payload: BreakdownClipboard = { type: 'jobos-breakdown', version: 1, items };
  return JSON.stringify(payload);
}

export type ParseResult =
  | { ok: true; items: FlatItem[] }
  | { ok: false; reason: 'invalid' | 'version' };

export function parseClipboard(text: string): ParseResult {
  try {
    const parsed = JSON.parse(text);
    if (parsed?.type !== 'jobos-breakdown' || !Array.isArray(parsed?.items)) {
      return { ok: false, reason: 'invalid' };
    }
    if (parsed.version !== 1) {
      return { ok: false, reason: 'version' };
    }
    for (const item of parsed.items) {
      if (!item.id || !item.type || !item.data) return { ok: false, reason: 'invalid' };
    }
    return { ok: true, items: parsed.items };
  } catch {
    return { ok: false, reason: 'invalid' };
  }
}

export function cloneAndTransformItems(
  items: FlatItem[],
  jobRates: Rates,
  typeMeta: SubItemTypeMetaMap,
  categories: SubItemCategory[],
): FlatItem[] {
  const projectIdMap = new Map<string, string>();
  for (const item of items) {
    if (item.type === 'project-header') {
      projectIdMap.set(item.id, crypto.randomUUID());
    }
  }

  return items.map((item) => {
    const newId =
      item.type === 'project-header' ? projectIdMap.get(item.id)! : crypto.randomUUID();

    const newProjectId =
      item.projectId !== null ? (projectIdMap.get(item.projectId) ?? null) : null;

    if (item.type === 'project-header') {
      return {
        id: newId,
        type: 'project-header' as const,
        projectId: null,
        data: { ...(item.data as any), id: newId, lineItems: [] },
      };
    }

    const oldData = item.data as LineItemData;
    const newSubItems: SubItemForm[] = oldData.subItems.map((sub) => {
      const newSub = { ...sub, id: crypto.randomUUID() };
      delete (newSub as any).markupRate;
      return newSub;
    });
    const totalCost = calculateTotalCost(newSubItems, jobRates, typeMeta, categories);

    const newData: LineItemData = {
      id: newId,
      category: oldData.category,
      description: oldData.description,
      totalCost,
      subItems: newSubItems,
      // templateId and itemDate intentionally omitted (stripped)
    };

    return {
      id: newId,
      type: 'line-item' as const,
      projectId: newProjectId,
      data: newData,
    };
  });
}
